import { BehaviorSubject, type Observable } from 'rxjs';

/**
 * Multi-select state for tables. Forked from `@adornis/bh-util/data-view-selection.ts`
 * because BH's renderers are bound to `tr-*` theme components and BH's `'all'`
 * marker has a known "untick one collapses to empty" bug we don't want to inherit.
 * Selection is always a concrete ID list.
 */
export type Selection = string[];

const sameSet = (a: string[], b: string[]) => {
  if (a.length !== b.length) return false;
  const sa = new Set(a);
  for (const x of b) if (!sa.has(x)) return false;
  return true;
};

export const createSelectionStore = () => {
  const subject = new BehaviorSubject<Selection>([]);
  return {
    observable: subject as Observable<Selection>,
    value: () => subject.value,
    set: (next: Selection) => {
      if (sameSet(subject.value, next)) return;
      subject.next(next);
    },
    toggle: (id: string, selected: boolean) => {
      const current = subject.value;
      if (selected) {
        if (current.includes(id)) return;
        subject.next([...current, id]);
      } else {
        if (!current.includes(id)) return;
        subject.next(current.filter(it => it !== id));
      }
    },
    toggleSubset: (subsetIds: string[], selected: boolean) => {
      const current = subject.value;
      if (selected) {
        const next = Array.from(new Set([...current, ...subsetIds]));
        if (next.length === current.length) return;
        subject.next(next);
      } else {
        const subset = new Set(subsetIds);
        const next = current.filter(id => !subset.has(id));
        if (next.length === current.length) return;
        subject.next(next);
      }
    },
    clear: () => {
      if (subject.value.length === 0) return;
      subject.next([]);
    },
  };
};

export type SelectionStore = ReturnType<typeof createSelectionStore>;

export const isIDSelected = (current: Selection, id: string) => current.includes(id);
export const selectionCount = (current: Selection) => current.length;
export const isEmptySelection = (current: Selection) => current.length === 0;
